/**
 * Trang quản trị danh sách BĐS — CRUD và duyệt tin.
 *
 * Luồng dữ liệu:
 * - useAdminProperties: danh sách có phân trang, bộ lọc, thao tác xóa/duyệt.
 * - useAuth: xác định quyền tạo tin, duyệt tin, xem thông tin nhanh theo role.
 * - Modal state cục bộ điều khiển create/edit/delete/info; sau lưu gọi reloadPropertyList.
 */
import { useMemo, useState } from 'react';
import { AdminPropertyCard } from '../../components/admin/AdminPropertyCard';
import { AdminPropertiesSkeleton } from '../../components/admin/AdminPropertiesSkeleton';
import { AdminPropertyCreateModal } from '../../components/admin/AdminPropertyCreateModal';
import { AdminPropertyDeleteModal } from '../../components/admin/AdminPropertyDeleteModal';
import { AdminPropertyEditModal } from '../../components/admin/AdminPropertyEditModal';
import { AdminPropertyInfoModal } from '../../components/admin/AdminPropertyInfoModal';
import { EmptyState } from '../../components/Common/EmptyState';
import { Pagination } from '../../components/Common/Pagination';
import { useAdminProperties } from '../../features/admin/properties/hooks/useAdminProperties';
import { useAuth } from '../../hooks/useAuth';
import { PROPERTY_STATUS_OPTIONS } from '../../shared/constants/property';
import { ADMIN_ROLES, USER_ROLES } from '../../shared/constants/users';
import { formatText, TEXT } from '../../shared/languages';

/** Bộ lọc nhanh theo trạng thái xuất bản (chip buttons) */
const QUICK_PUBLICATION_FILTERS = [
  { value: '', labelKey: 'filterAll' },
  { value: 'pending', labelKey: 'filterPending' },
  { value: 'published', labelKey: 'filterPublished' },
  { value: 'draft', labelKey: 'filterDraft' },
];

export const AdminPropertiesPage = () => {
  const {
    propertyList,
    pagination,
    filters,
    searchInput,
    setSearchInput,
    isLoadingProperties,
    deletingPropertyId,
    approvingPropertyId,
    hasActiveFilters,
    updateFilter,
    changePage,
    deleteProperty,
    approveProperty,
    reloadPropertyList,
  } = useAdminProperties();
  const { user } = useAuth();

  /** Phân quyền theo role — admin trở lên được tạo tin */
  const canCreateProperty = ADMIN_ROLES.includes(user?.role);
  const canApproveProperty = user?.role === USER_ROLES.superAdmin;
  const canViewQuickInfo = user?.role === USER_ROLES.superAdmin;

  /** State điều khiển các modal — lưu id/object tin đang thao tác */
  const [selectedInfoPropertyId, setSelectedInfoPropertyId] = useState(null);
  const [editingPropertyId, setEditingPropertyId] = useState(null);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const admin = TEXT.admin;
  const propertiesText = admin.properties;

  /** Nhãn tổng số kết quả hiển thị dưới tiêu đề trang */
  const summaryLabel = useMemo(
    () =>
      formatText(propertiesText.resultsCount, {
        count: pagination?.total ?? propertyList.length,
      }),
    [pagination?.total, propertyList.length, propertiesText.resultsCount]
  );

  /** Xác nhận xóa tin qua modal → gọi API deleteProperty */
  const handleConfirmDelete = async () => {
    if (!propertyToDelete) {
      return;
    }

    await deleteProperty(propertyToDelete.id);
    setPropertyToDelete(null);
  };

  /** Duyệt tin — chỉ superAdmin mới thấy nút trên AdminPropertyCard */
  const handleApprove = async (property) => {
    await approveProperty(property.id);
  };

  return (
    <div className="admin-properties-page">
      <div className="admin-heading admin-properties-heading">
        <div className="admin-properties-heading-copy">
          <span className="eyebrow">{admin.propertiesEyebrow}</span>
          <h1>{admin.propertiesTitle}</h1>
          <p className="admin-properties-subtitle">{summaryLabel}</p>
        </div>
        {canCreateProperty && (
          <button
            type="button"
            className="button admin-properties-add-btn"
            onClick={() => setIsCreateOpen(true)}
          >
            {admin.addNew}
          </button>
        )}
      </div>

      <div className="admin-properties-toolbar">
        <label className="admin-properties-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            inputMode="search"
            enterKeyHint="search"
            role="searchbox"
            name="admin-properties-filter"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder={propertiesText.searchPlaceholder}
          />
        </label>

        <div className="admin-properties-filter-row">
          {QUICK_PUBLICATION_FILTERS.map((item) => (
            <button
              key={item.value || 'all'}
              type="button"
              className={`admin-properties-chip ${filters.publication_status === item.value ? 'is-active' : ''}`}
              onClick={() => updateFilter('publication_status', item.value)}
            >
              {propertiesText[item.labelKey]}
            </button>
          ))}
        </div>

        <div className="admin-properties-select-row">
          <label className="admin-properties-select">
            <span>{propertiesText.filterListingStatus}</span>
            <select
              value={filters.status}
              onChange={(event) => updateFilter('status', event.target.value)}
            >
              <option value="">{propertiesText.filterAll}</option>
              {PROPERTY_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {isLoadingProperties ? (
        <AdminPropertiesSkeleton />
      ) : propertyList.length ? (
        <>
          <div className="admin-property-list">
            {propertyList.map((property) => (
              <AdminPropertyCard
                key={property.id}
                property={property}
                canApproveProperty={canApproveProperty}
                canViewQuickInfo={canViewQuickInfo}
                deletingPropertyId={deletingPropertyId}
                approvingPropertyId={approvingPropertyId}
                onApprove={handleApprove}
                onDelete={setPropertyToDelete}
                onViewInfo={(property) => setSelectedInfoPropertyId(property.id)}
                onEdit={(property) => setEditingPropertyId(property.id)}
              />
            ))}
          </div>

          <Pagination pagination={pagination} isLoading={isLoadingProperties} onPageChange={changePage} />
        </>
      ) : (
        <EmptyState
          title={hasActiveFilters ? propertiesText.noResultsTitle : admin.propertiesEmptyTitle}
          message={
            hasActiveFilters
              ? propertiesText.noResultsMessage
              : canCreateProperty
                ? admin.propertiesEmptyMessageCreate
                : admin.propertiesEmptyMessageView
          }
          action={
            canCreateProperty && !hasActiveFilters ? (
              <button type="button" className="button" onClick={() => setIsCreateOpen(true)}>
                {admin.addListing}
              </button>
            ) : null
          }
        />
      )}

      <AdminPropertyInfoModal
        isOpen={Boolean(selectedInfoPropertyId)}
        propertyId={selectedInfoPropertyId}
        onClose={() => setSelectedInfoPropertyId(null)}
      />

      <AdminPropertyEditModal
        isOpen={Boolean(editingPropertyId)}
        propertyId={editingPropertyId}
        canReviewProperty={canApproveProperty}
        userRole={user?.role}
        onClose={() => setEditingPropertyId(null)}
        onSaved={() => {
          setEditingPropertyId(null);
          reloadPropertyList();
        }}
      />

      <AdminPropertyCreateModal
        isOpen={isCreateOpen}
        canReviewProperty={canApproveProperty}
        userRole={user?.role}
        onClose={() => setIsCreateOpen(false)}
        onSaved={() => {
          setIsCreateOpen(false);
          reloadPropertyList();
        }}
      />

      <AdminPropertyDeleteModal
        isOpen={Boolean(propertyToDelete)}
        property={propertyToDelete}
        isDeleting={Boolean(propertyToDelete && deletingPropertyId === propertyToDelete.id)}
        onClose={() => setPropertyToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};
